import * as React from 'react'

import {
  AllowanceIncidenceFragment,
  TotalDaysAllowanceIncidencesDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import StatsRow from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/StatsRow'
import { isHourlyFixedBalanceAllowance } from 'modules/timeoff/components/Leaves/Overview/formatCounterAmount'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type {
  Cycle,
  ParsedAllowanceStat,
} from 'modules/timeoff/components/Leaves/Overview/NewStatsCounter/allowanceStatUtils'

import TotalTooltip from './TotalTooltip'
import { computeTotalAmount } from './totalAmount'

interface Props {
  allowance: Allowance
  employeeId: string
  selectedCycle: Cycle
  selectedStat: ParsedAllowanceStat
}

function filterIncidencesByCycle<T extends Pick<AllowanceIncidenceFragment, 'effectiveOn'>>(
  incidences: T[],
  cycle: Cycle,
): T[] {
  return incidences.filter((incidence) => {
    const effectiveOn = moment(incidence.effectiveOn)
    return cycle.incidenceDateCheck(effectiveOn)
  })
}

const TotalDays = ({ allowance, employeeId, selectedCycle, selectedStat }: Props) => {
  const { has_dev_timeoff_new_totalcalculations: useNewTotalCalculations } = useFeatures([
    'dev_timeoff_new_totalcalculations',
  ])

  const {
    prorated_allowance_days: proratedDays,
    carry_overs: carryOvers,
    incidences: totalAdjustment,
    accumulated_carry_over: totalCarryover,
  } = selectedStat.attributes

  const {
    data,
    loading: incidencesLoading,
    error: incidencesError,
  } = useQuery(TotalDaysAllowanceIncidencesDocument, {
    variables: {
      employeeIds: [employeeId],
      timeoffAllowanceIds: [allowance.attributes.id],
    },
    extra: {
      errorFlash: true,
      errorMessage: i18n.t('errors.generic_error'),
    },
  })

  const allIncidences = React.useMemo(() => {
    return data?.timeoff?.allowanceIncidencesConnection?.nodes ?? []
  }, [data])

  const incidences = React.useMemo(() => {
    return filterIncidencesByCycle(allIncidences, selectedCycle)
  }, [allIncidences, selectedCycle])

  const showIncidenceBreakdown = !incidencesLoading && !incidencesError

  const displayedTotal = computeTotalAmount({
    useNewTotalCalculations,
    total: selectedStat.attributes.total,
    proratedDays,
    totalAdjustment,
    totalCarryover,
  })

  const title = allowance.isDays
    ? i18n.t('time_off.new_counter.total_row.days')
    : i18n.t('time_off.new_counter.total_row.hours')

  return (
    <StatsRow
      title={title}
      amount={displayedTotal}
      isHourlyFixedBalance={isHourlyFixedBalanceAllowance(allowance)}
      tooltip={
        <TotalTooltip
          accrued={proratedDays}
          allowance={allowance}
          carryOvers={carryOvers}
          incidences={showIncidenceBreakdown ? incidences : []}
          stat={selectedStat}
          thisCycle={selectedCycle.yearLabel}
        />
      }
    />
  )
}

export { filterIncidencesByCycle }

export default TotalDays
